import {
  TextPhoto,
  TextPhotoWithDetails,
  PropertyValue,
  PropertyVariable,
  AppliedProperty,
  ResourceRow,
} from '@oare/types';
import sl from '@/serviceLocator';
import { photoKeyPrefix } from '@oare/oare';

/**
 * The photos that can be uploaded: a file with a side and a view. A photo
 * whose file was cleared or rejected stays in the list as an incomplete
 * entry until the user removes it.
 */
export const completePhotos = (photos: TextPhoto[]): TextPhoto[] =>
  photos.filter(photo => !!photo.upload && !!photo.side && !!photo.view);

export const addDetailsToTextPhotos = async (
  excavationPrefix: string | null,
  excavationNumber: string | null,
  museumPrefix: string | null,
  museumNumber: string | null,
  publicationPrefix: string | null,
  publicationNumber: string | null,
  photos: TextPhoto[],
  // Names this page already uploaded, with the file uploaded under each.
  uploaded: Map<string, File | undefined> = new Map()
): Promise<TextPhotoWithDetails[]> => {
  const server = sl.get('serverProxy');

  const photoNames = await Promise.all(
    photos.map(photo =>
      generatePhotoName(
        excavationPrefix,
        excavationNumber,
        museumPrefix,
        museumNumber,
        publicationPrefix,
        publicationNumber,
        photo
      )
    )
  );

  const propertiesTree = await server.getTaxonomyPropertyTree();

  const photosWithNamesUncorrected: TextPhotoWithDetails[] = photos.map(
    (photo, idx) => {
      const sideValueUuids = getSideValueUuid(photo.side);
      const viewAngleValueUuid = getViewAngleValueUuid(photo.view);
      const valueUuids = [...sideValueUuids, viewAngleValueUuid];

      const properties: AppliedProperty[] = [];

      const searchTree = (
        node: PropertyVariable | PropertyValue,
        searchValue: string,
        existingFinds: string[]
      ): PropertyVariable | PropertyValue | null => {
        if (node.uuid === searchValue) {
          return node;
        }
        if (
          (node as PropertyVariable).values &&
          (node as PropertyVariable).values.length > 0
        ) {
          for (
            let i = 0;
            i < (node as PropertyVariable).values.length;
            i += 1
          ) {
            const result = searchTree(
              (node as PropertyVariable).values[i],
              searchValue,
              existingFinds
            );
            if (result) {
              properties.unshift({
                variableRow: node as PropertyVariable,
                valueRow: (node as PropertyVariable).values[i],
                value: null,
                sourceUuid: '',
                objectUuid: null,
                objectDisplay: null,
              });
              return result;
            }
          }
        } else if (
          (node as PropertyValue).variables &&
          (node as PropertyValue).variables.length > 0
        ) {
          for (
            let i = 0;
            i < (node as PropertyValue).variables.length;
            i += 1
          ) {
            const result = searchTree(
              (node as PropertyValue).variables[i],
              searchValue,
              existingFinds
            );
            if (result) {
              return result;
            }
          }
        }
        return null;
      };

      valueUuids.forEach(uuid => {
        const existingFinds: string[] = [];
        propertiesTree.tree.variables.map(v =>
          searchTree(v, uuid, existingFinds)
        );
      });

      const adjustedProperties: AppliedProperty[] = [];
      properties.forEach(prop => {
        const propUuidString = `${prop.variableRow.uuid}-${
          prop.valueRow!.uuid
        }`;
        if (
          !adjustedProperties
            .map(
              adjProp => `${adjProp.variableRow.uuid}-${adjProp.valueRow!.uuid}`
            )
            .includes(propUuidString)
        ) {
          adjustedProperties.push(prop);
        }
      });

      return {
        ...photo,
        name: photoNames[idx],
        properties: adjustedProperties,
      };
    }
  );
  return correctPhotoNames(photosWithNamesUncorrected, uploaded);
};

const getSideValueUuid = (side: string | number | undefined): string[] => {
  switch (side) {
    case 1:
      return ['8f0bad4b-7887-11ec-bcc3-0282f921eac9'];
    case 2:
      return ['8f1ce20d-7887-11ec-bcc3-0282f921eac9'];
    case 3:
      return ['8f2bf270-7887-11ec-bcc3-0282f921eac9'];
    case 4:
      return ['8f3af02a-7887-11ec-bcc3-0282f921eac9'];
    case 5:
      return ['8f492346-7887-11ec-bcc3-0282f921eac9'];
    case 6:
      return ['8f57d3f1-7887-11ec-bcc3-0282f921eac9'];
    case 'a':
      return ['8f67224c-7887-11ec-bcc3-0282f921eac9'];
    case 'b':
      return ['8f768f01-7887-11ec-bcc3-0282f921eac9'];
    case 'c':
      return ['8f84f1db-7887-11ec-bcc3-0282f921eac9'];
    case 'd':
      return ['8f92e17c-7887-11ec-bcc3-0282f921eac9'];
    case 'e':
      return ['8fa0cb1d-7887-11ec-bcc3-0282f921eac9'];
    case 'f':
      return ['8faf16bd-7887-11ec-bcc3-0282f921eac9'];
    case 'x':
      return [
        '8f0bad4b-7887-11ec-bcc3-0282f921eac9',
        '8f1ce20d-7887-11ec-bcc3-0282f921eac9',
        '8f2bf270-7887-11ec-bcc3-0282f921eac9',
        '8f3af02a-7887-11ec-bcc3-0282f921eac9',
        '8f492346-7887-11ec-bcc3-0282f921eac9',
        '8f57d3f1-7887-11ec-bcc3-0282f921eac9',
      ];
    case 'h':
      return [
        '8f0bad4b-7887-11ec-bcc3-0282f921eac9',
        '8f1ce20d-7887-11ec-bcc3-0282f921eac9',
        '8f2bf270-7887-11ec-bcc3-0282f921eac9',
        '8f3af02a-7887-11ec-bcc3-0282f921eac9',
        '8f492346-7887-11ec-bcc3-0282f921eac9',
        '8f57d3f1-7887-11ec-bcc3-0282f921eac9',
        '661beb94-d53d-11ec-9ff3-0282f921eac9',
      ];
    case 8:
      return ['661c4328-d53d-11ec-9ff3-0282f921eac9'];
    case 9:
      return ['661c24aa-d53d-11ec-9ff3-0282f921eac9'];
    default:
      return [];
  }
};

const getViewAngleValueUuid = (view: string | undefined): string => {
  switch (view) {
    case 'u':
      return '8fbea91c-7887-11ec-bcc3-0282f921eac9';
    case 'd':
      return '8fce7051-7887-11ec-bcc3-0282f921eac9';
    case 'l':
      return '8fddfa11-7887-11ec-bcc3-0282f921eac9';
    case 'i':
      return '6615c8ec-d53d-11ec-9ff3-0282f921eac9';
    case 'h':
      return '661b39d8-d53d-11ec-9ff3-0282f921eac9';
    case 'j':
      return '661ba255-d53d-11ec-9ff3-0282f921eac9';
    case 'z':
      return '901e8362-7887-11ec-bcc3-0282f921eac9';
    case 'n':
      return '661be7da-d53d-11ec-9ff3-0282f921eac9';
    case 's':
      return '10d78a47-d53e-11ec-9ff3-0282f921eac9';
    case 'fr':
      return '9001b7a5-7887-11ec-bcc3-0282f921eac9';
    case 'fl':
      return '8ff31f12-7887-11ec-bcc3-0282f921eac9';
    case 'cx':
      return '902d78c7-7887-11ec-bcc3-0282f921eac9';
    case 'du':
      return '900fd50d-7887-11ec-bcc3-0282f921eac9';
    default:
      return '';
  }
};

const extensionPart = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * The extension of a photo key: the file name's extension, else the MIME
 * subtype (both lowercased, letters and digits only, so `image/svg+xml`
 * gives `svgxml`), else `jpg`. Photo keys must end in `.<letters/digits>`.
 */
export const photoExtension = (file?: File): string => {
  const name = file?.name || '';
  const dot = name.lastIndexOf('.');
  const fromName = dot >= 0 ? extensionPart(name.slice(dot + 1)) : '';
  if (fromName) {
    return fromName;
  }
  const type = file?.type || '';
  const fromType = extensionPart(type.slice(type.lastIndexOf('/') + 1));
  return fromType || 'jpg';
};

export const generatePhotoName = async (
  excavationPrefix: string | null,
  excavationNumber: string | null,
  museumPrefix: string | null,
  museumNumber: string | null,
  publicationPrefix: string | null,
  publicationNumber: string | null,
  photo: TextPhoto
): Promise<string> => {
  const store = sl.get('store');
  const server = sl.get('serverProxy');
  const { user } = store.getters;

  // Shared with the create route, which accepts only keys with this prefix.
  const prefix =
    photoKeyPrefix(
      {
        excavationPrefix,
        excavationNumber,
        museumPrefix,
        museumNumber,
        publicationPrefix,
        publicationNumber,
      },
      user || { firstName: '', lastName: '' }
    ) || '';
  const preDesignatorText = `${prefix}s-${photo.side}-${photo.view}-`;

  const designator = await server.getNextImageDesignator(preDesignatorText);

  return `${preDesignatorText}${designator}.${photoExtension(photo.upload)}`;
};

/**
 * Gives every photo of one import its own designator. The server hands out
 * the next free designator per prefix (side and view included), and it does
 * not know about this import's other photos yet, so two photos with the same
 * side and view come back with the same name. Later ones move to the next
 * unused designator and keep their extension: `...-s-1-u-1.jpeg`,
 * `...-s-1-u-2.jpeg`. The create route accepts only keys of this shape.
 */
export const correctPhotoNames = (
  photos: TextPhotoWithDetails[],
  uploaded: Map<string, File | undefined> = new Map()
) => {
  const usedDesignators: { [preDesignatorText: string]: Set<number> } = {};
  // A name this page already uploaded a different file under is taken: the
  // upload route never overwrites an object, so a replaced file needs a new
  // name. The same file keeps its name, and its upload is skipped.
  const takenByUpload = (name: string, file?: File) =>
    uploaded.has(name) && uploaded.get(name) !== file;
  return photos.map(photo => {
    const cut = photo.name.lastIndexOf('-') + 1;
    const preDesignatorText = photo.name.slice(0, cut);
    const rest = photo.name.slice(cut);
    const dot = rest.indexOf('.');
    const extension = dot >= 0 ? rest.slice(dot) : '';
    let designator = Number(dot >= 0 ? rest.slice(0, dot) : rest) || 1;
    usedDesignators[preDesignatorText] =
      usedDesignators[preDesignatorText] || new Set();
    const used = usedDesignators[preDesignatorText];
    while (
      used.has(designator) ||
      takenByUpload(
        `${preDesignatorText}${designator}${extension}`,
        photo.upload
      )
    ) {
      designator += 1;
    }
    used.add(designator);
    const name = `${preDesignatorText}${designator}${extension}`;
    return name === photo.name ? photo : { ...photo, name };
  });
};

/**
 * The same photo name with the next designator:
 * `nk-1234-fibr-s-1-u-1.jpeg` gives `nk-1234-fibr-s-1-u-2.jpeg`.
 */
export const nextDesignatorName = (name: string): string => {
  const cut = name.lastIndexOf('-') + 1;
  const rest = name.slice(cut);
  const dot = rest.indexOf('.');
  const extension = dot >= 0 ? rest.slice(dot) : '';
  const designator = Number(dot >= 0 ? rest.slice(0, dot) : rest) || 0;
  return `${name.slice(0, cut)}${designator + 1}${extension}`;
};

/** Upload the photos and update only each photo's own resource after renaming. */
export const uploadPendingTextPhotos = async (
  photos: TextPhotoWithDetails[],
  // buildTables creates one resource per photo in this same order.
  resources: ResourceRow[],
  uploaded: Map<string, File | undefined>,
  upload: (photo: TextPhotoWithDetails) => Promise<unknown>
): Promise<void> => {
  if (photos.length !== resources.length) {
    throw new Error('The photo resources must be rebuilt before uploading.');
  }
  // Reserve every sibling's name before any upload starts. Keep reservations
  // for attempted names too, including S3 objects from earlier failed imports.
  const designatorKey = (name: string) => name.slice(0, name.lastIndexOf('.'));
  const reserved = new Set(
    [...photos.map(photo => photo.name), ...uploaded.keys()].map(designatorKey)
  );
  const results = await Promise.allSettled(
    photos.map(async (photo, index) => {
      if (
        uploaded.has(photo.name) &&
        uploaded.get(photo.name) === photo.upload
      ) {
        return;
      }
      const resource = resources[index];
      uploaded.delete(photo.name);
      const maxNameAttempts = 50;
      for (let attempt = 1; ; attempt += 1) {
        try {
          // Choose a new name only after S3 rejects the previous attempt.
          // eslint-disable-next-line no-await-in-loop
          await upload(photo);
          uploaded.set(photo.name, photo.upload);
          return;
        } catch (err) {
          const taken = (err as { code?: string }).code === 'photo-name-taken';
          if (!taken || attempt >= maxNameAttempts) {
            throw err;
          }
          let newName = nextDesignatorName(photo.name);
          while (reserved.has(designatorKey(newName))) {
            newName = nextDesignatorName(newName);
          }
          reserved.add(designatorKey(newName));
          // A retry can pass another photo's original name; links are mutable
          // and cannot identify which resource belongs to this photo.
          resource.link = newName;
          photo.name = newName;
        }
      }
    })
  );
  // Finish sibling uploads before allowing Retry to start another batch.
  const failure = results.find(result => result.status === 'rejected');
  if (failure?.status === 'rejected') {
    throw failure.reason;
  }
};
